import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    Image,
    SectionList,
    ScrollView
} from 'react-native-web';
import * as Animatable from 'react-native-animatable';
import moment from 'moment-timezone';
import AwesomeIcon from 'react-native-vector-icons/dist/FontAwesome';
import PropTypes from 'prop-types';

// Child Components
import SavedPayment from './component_saved_payment';

// Components
import { Button, InScreenModal, NavBar } from '../../components';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';
import {
    getSubscriptionDateStr,
    hasSubscriptionPaymentFailure,
    vendorFreeTrialCheck
} from '../../utils/util_truck_helpers';

// APIs
import {
    getAvailableTiers,
    getNewPaymentMethodIntentData,
    getTierProration,
    requestTierUpgrade,
    sendCancellationRequest
} from '../../modules/api_tiers';

// Configs
import GLOBAL from '../../configs/config_global';
import { getGlobalState, setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';
import { cloneDeep } from 'lodash';
import { loadStripe } from '@stripe/stripe-js';
import LoadingView from '../../components/component_loading_view';
import { StripePaymentForm } from '../../components/component_stripe_modal';
import { Elements } from '@stripe/react-stripe-js';
import { showStripeInfo } from '../../utils/util_credit_card';
import * as RootNavigation from '../../navigation/RootNavigation';

const SubscriptionTier = (props, ref) => {
    const {
        onCardSelectedForTier = (tier = 0) => {},
        onPaymentDone = () => {}
    } = props;

    // region Default Tier List
    const DEFAULT_TIER_LIST = [
        {
            tier: 0,
            title: 'Free',
            price: 0,
            newPrice: 0,
            data: [0],
            scrollIndex: 0,
            stripeProductID: null
        },
        {
            tier: 1,
            title: 'Mild',
            price: 0,
            newPrice: 20,
            data: [1],
            scrollIndex: 4,
            stripeProductID: null
        },
        {
            tier: 2,
            title: 'Medium',
            price: 0,
            newPrice: 35,
            data: [2],
            scrollIndex: 7,
            stripeProductID: null
        },
        {
            tier: 3,
            title: 'Hot',
            price: 0,
            newPrice: 45,
            data: [3],
            scrollIndex: 11,
            stripeProductID: null
        }
    ];
    // endregion

    // region Refs
    const mainList = useRef(null);
    const savedPaymentModal = useRef(null);
    // endregion

    // region Stripe Key
    const stripeLoader = useMemo(() => loadStripe(getGlobalState(STATE_KEYS.IS_DEV) ? process.env.REACT_APP_KEY_STRIPE_PUBLISHABLE_SANDBOX : process.env.REACT_APP_KEY_STRIPE_PUBLISHABLE_PRODUCTION), []);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [tierList, setTierList] = useState(DEFAULT_TIER_LIST);
    const [isLoadingData, setLoadingData] = useState(true);
    const [allSamePrice, setAllSamePrice] = useState(false);

    const [isLoadingPaymentFlow, setLoadingPaymentFlow] = useState(false);
    const [stripeClientSecret, setStripeClientSecret] = useState('');
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [isSavingPayment, setSavingPayment] = useState(false);
    const [selectedTier, setSelectedTier] = useState(0);
    const [isLoadingUpgrade, setLoadingUpgrade] = useState(false);
    // endregion

    // region Derived Data
    const subDateStr = useMemo(() => getSubscriptionDateStr(), []);
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => goFetchTierData(), 500);
    }, []);
    // endregion

    // region Function - Get Tier Name
    const getTierName = (tier = 0) => {
        return(`Tier ${tier} (${tierList[tier]?.title})`);
    }
    // endregion

    // region Function - Fetch Tier Stripe Data
    const goFetchTierData = () => {
        setLoadingData(true);
        getAvailableTiers()
            .then((result) => {
                const newTierList = cloneDeep(tierList);

                // Get tier price
                Object.keys(result || {}).forEach((tierKey) => {
                    const tierItem = newTierList[Number(tierKey)];
                    const resultItem = result[tierKey] || {};
                    tierItem.stripeProductID = resultItem['product_id'];
                    tierItem.price = resultItem['price'];
                });

                // Check if all tiers are the same price
                let lastPrice = newTierList[1].price, allTiersSamePrice = true;
                newTierList.forEach((tier) => {
                    if ((tier.tier > 0) && (tier.price !== lastPrice)) {
                        allTiersSamePrice = false;
                        lastPrice = tier.price;
                    }
                });
                setAllSamePrice(allTiersSamePrice);

                // Save tier list
                setTierList(newTierList);
            })
            .catch((errorMsg) => showAppError(errorMsg))
            .finally(() => {
                setLoadingData(false);

                // Scroll the list to user's current tier
                const scrollIndex = tierList[GLOBAL.DATA.USER['tier']]?.scrollIndex;
                setTimeout(() => mainList.current?.scrollToLocation({ itemIndex: scrollIndex, viewPosition: 0 }), 500);
            });
    }
    // endregion

    // region Function - Go Upgrade to New Tier
    const goUpgradeToNewTier = (tier, newCharge = null, callback = () => {}) => {
        const tierName = getTierName(tier);
        const isFreeTrialTierSelection = newCharge === null;

        // Send upgrade request to server
        setLoadingUpgrade(true);
        requestTierUpgrade(tier)
            .then(() => {
                // Update global data
                if (isFreeTrialTierSelection) {
                    GLOBAL.DATA.USER['after_trial_tier'] = tier;
                    onCardSelectedForTier(tier);

                    // Refresh Profile Tab
                    setGlobalState(STATE_KEYS.PROFILE_REFRESH_TRIGGERED, true);
                }

                // Show user message
                showAlert({
                    text: (
                        isFreeTrialTierSelection ?
                            // Free Trial selecting tier for after trial period
                            `Heard. You subscribed to ${tierName}! Your payment of $${tierList[GLOBAL.DATA.USER['after_trial_tier']]?.price} will be processed on ${subDateStr}`
                            :
                            // Active vendor upgrading
                            `Upgraded successfully to ${tierName}` + (newCharge < 0 ? '' : `, and your card on file will be charged the remaining balance for this month.`)
                    ),
                    cancelButton: { text: 'Got it', onPress: callback }
                })
            })
            .catch((errorMsg) => showAppError(errorMsg))
            .finally(() => setLoadingUpgrade(false));
    }
    // endregion

    // region Function - Fetch Intent for New Payment Method
    const goFetchClientSecret = useCallback((tier) => {
        setLoadingPaymentFlow(true);
        setTimeout(() => {
            getNewPaymentMethodIntentData(tier)
                .then((result) => {
                    setStripeClientSecret(result['client_secret']);
                    setShowStripeModal(true);
                    setSelectedTier(tier);
                })
                .catch((error) => showAppError(error))
                .finally(() => setLoadingPaymentFlow(false));
        }, 1000);
    }, []);
    // endregion

    // region Function - Send Upgrade Request (only for Active User)
    const sendUpgradeRequest = (tier = 1) => {
        // Case 1: Downgrading/Cancelling
        const tierName = getTierName(tier);
        if (tier <= GLOBAL.DATA.USER['tier']) {
            showAlert({
                text: (
                    tier < GLOBAL.DATA.USER['tier'] ?
                        `If you would like to downgrade from your current tier to<br/><p style="color: blue; font-weight: bold;">${tierName}</p>Please contact us at<br/><b>vendor.support@gotruckster.com</b>`
                        :
                        'If you would like to cancel your Truckster Vendor subscription, please contact us at<br/><b>vendor.support@gotruckster.com</b>'
                )
            });

            // Send request to Slack
            (tier === GLOBAL.DATA.USER['tier']) && sendCancellationRequest().catch(() => {});
            return;
        }

        // Case 2: Upgrading to new tier
        if (GLOBAL.DATA.USER['tier'] > 0) {
            // Has at least one card added
            if (GLOBAL.DATA.USER['total_stripe_cards']) {
                // Get proration data to get new price difference after upgrading
                setLoadingPaymentFlow(true);
                getTierProration(tier)
                    .then((result) => {
                        // Calculate new charge for upgrading
                        const newCharge = Number(((result['cost_for_time_spent']?.amount - Math.abs(Number(result['credit_for_unused_times']?.amount))) / 100).toFixed(2));

                        // Show confirmation alert
                        showAlert({
                            text: (
                                (
                                    newCharge < 0 ?
                                        `You have $${Math.abs(newCharge)} of unused credit in your subscription account, which will be put toward your upcoming payment.`
                                        :
                                        `Your balance due for this month will be $${newCharge}`
                                ) + `<br/><br/>Confirm upgrading to Tier ${tierName}?`
                            ),
                            cancelButton: { text: 'Cancel' },
                            confirmButton: { text: 'Confirm', color: 'red', onPress: () => goUpgradeToNewTier(tier, newCharge, onPaymentDone) }
                        });
                    })
                    .catch((errorMsg) => showAppError(errorMsg))
                    .finally(() => setLoadingPaymentFlow(false));
            }
            else {
                goFetchClientSecret(tier);
            }
            return;
        }

        // Case 3: Free (and status=active) user
        if (GLOBAL.DATA.USER['tier'] === 0) {
            // Has at least one card added
            if (GLOBAL.DATA.USER['total_stripe_cards']) {
                showAlert({
                    text: `Confirm upgrading to ${tierName}?`,
                    cancelButton: { text: 'Cancel' },
                    confirmButton: { text: 'Confirm', color: 'red', onPress: () => goUpgradeToNewTier(tier, tierList[tier]?.price || 1, onPaymentDone) }
                });
            }
            // No card added
            else {
                goFetchClientSecret(tier);
            }
        }
    }
    // endregion

    // region Render Stripe Modal
    const renderStripeModal = () => {
        return(
            <InScreenModal visible={showStripeModal} style={{ marginVertical: '5%' }}>
                {/* Nav Bar */}
                <NavBar
                    title={'Set Up Payment Method'}
                    subTitle={'Powered by Stripe'}
                    hideBorder={false}
                    leftButtonOnPress={() => setShowStripeModal(false)}
                    rightButtonIconName={'info'}
                    rightButtonColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                    rightButtonOnPress={() => showStripeInfo()}
                />

                {/* Stripe Payment Form */}
                <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 24 }}>
                    {
                        !!stripeClientSecret?.trim() &&
                        <Elements stripe={stripeLoader} options={{ clientSecret: stripeClientSecret }}>
                            <StripePaymentForm
                                stripeClientSecret={stripeClientSecret}
                                onStripeLoading={setSavingPayment}
                                onResult={(result) => {
                                    if (result.error) {
                                        if (result.error.code !== 'Canceled') showAppError(`${result.error.message}<br/><br/>[Error Code: ${result.error.code}]`);
                                    }
                                    else {
                                        onPaymentDone();

                                        // Show user message
                                        showAlert({
                                            text: `Upgraded successfully to ${getTierName(selectedTier)}, your card on file will be charged the remaining balance for this month.`,
                                            cancelButton: { text: 'Got it', onPress: () => setShowStripeModal(false) }
                                        })
                                    }
                                }}
                            />
                        </Elements>
                    }
                </ScrollView>

                {/* Loading View */}
                {
                    isSavingPayment &&
                    <LoadingView isLoading={true} description={'Saving payment method'} isAbsolute={true} />
                }
            </InScreenModal>
        );
    }
    // endregion

    // region Render Same Price Notice
    const renderSamePriceNotice = () => (
        <View
            style={{
                width: '100%', paddingHorizontal: 24, paddingVertical: 24, backgroundColor: GLOBAL.COLOR.BASIC.PURPLE,
                flexDirection: 'row', alignItems: 'center'
            }}>
            <AwesomeIcon name={'info-circle'} size={28} color={'white'} />
            <Text style={{ fontSize: 16, fontWeight: '450', color: 'white', marginHorizontal: 16, lineHeight: 26, flex: 1 }}>
                Special offer for you: All tiers are currently ${tierList[1].price}!
            </Text>
        </View>
    )
    // endregion

    // region Render Section Header
    const renderSectionHeader = (section) => {
        const freeTrialData = vendorFreeTrialCheck();
        return(
            <View style={{ paddingTop: 16, backgroundColor: 'white' }}>
                {
                    (GLOBAL.DATA.USER['tier'] === section.tier) &&
                    <View style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE, borderTopLeftRadius: 6, borderTopRightRadius: 6, paddingHorizontal: 12, paddingVertical: 6, alignSelf: 'flex-start', marginLeft: 22 }}>
                        <Text style={{ color: 'white', letterSpacing: 0.5, fontSize: 10 }}>
                            YOUR CURRENT TIER {freeTrialData.isFreeTrial ? ' (FREE TRIAL)' : ''}
                        </Text>
                    </View>
                }

                <View style={{
                    width: '100%', backgroundColor: GLOBAL.COLOR.MAIN,
                    paddingHorizontal: 32, paddingVertical: 16,
                    flexDirection: 'row', alignItems: 'center',
                    borderRadius: 8
                }}>
                    <Text style={{ fontWeight: '800', fontSize: 20, color: 'white', letterSpacing: 0.4, flex: 1 }}>
                        Tier {section.tier} - {section.title}
                    </Text>

                    {
                        section.tier > 0 &&
                        <View style={{ alignItems: 'flex-end' }}>
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: '500' }}>
                                ${section.price} / month
                            </Text>
                        </View>
                    }
                </View>
            </View>
        );
    }
    // endregion

    // region Render Section Item
    const renderSectionItem = (tier = 1, index) => {
        let benefitComp = <View/>;
        const sameTier = tier === GLOBAL.DATA.USER['tier'];
        const freeTrialData = vendorFreeTrialCheck();
        const itemBulletPointStyle = { fontSize: 16, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 8, lineHeight: 24 };
        const itemHeaderStyle = { fontSize: 20, fontWeight: 'bold', marginTop: 24 };

        switch(tier) {
            case 1: {
                benefitComp =
                    <View style={{ alignSelf: 'stretch', marginTop: 16 }}>
                        <Text style={[itemHeaderStyle, { marginTop: 0 }]}>
                            Marketing
                        </Text>

                        <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 8, fontSize: 16, lineHeight: 28 }}>
                            {
                                '• Respond to customer reviews.'
                            }
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Catering
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            • Specific requests.
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Reporting
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            • Marketing dashboard analytics.
                        </Text>
                    </View>;
                break;
            }
            case 2: {
                benefitComp =
                    <View style={{ alignSelf: 'stretch', marginTop: 16 }}>
                        <Text style={[itemHeaderStyle, { marginTop: 0 }]}>
                            Marketing
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            • Social media promotion.
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Catering
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            {
                                '• Get recommended for additional catering leads.\n' +
                                `• Truckster Hoods (Places that don't require ordering).`
                            }
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Reporting
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            {
                                '• Analytics.\n' +
                                `• Monthly email reports.`
                            }
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Community
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            • Chat board.
                        </Text>
                    </View>;
                break;
            }
            case 3: {
                benefitComp =
                    <View style={{ alignSelf: 'stretch', marginTop: 16 }}>
                        <Text style={[itemHeaderStyle, { marginTop: 0 }]}>
                            Marketing
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            • Featured trucks (rotating).
                        </Text>

                        <Text style={itemHeaderStyle}>
                            Ordering
                        </Text>

                        <Text style={itemBulletPointStyle}>
                            {
                                '• Online and mobile ordering.\n' +
                                '• Pre-ordering options.\n' +
                                '• Pickup time options.\n' +
                                '• Sales tax reporting.'
                            }
                        </Text>

                        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, alignSelf: 'stretch' }}>
                            <View style={{ paddingHorizontal: 8, paddingVertical: 4, marginRight: 6, backgroundColor: 'rgba(250, 57, 61, 0.2)', borderRadius: 6 }}>
                                <Text style={{ fontWeight: 'bold', fontSize: 12, color: GLOBAL.COLOR.MAIN }}>
                                    Special
                                </Text>
                            </View>

                            <Text style={{ fontWeight: 'bold', flex: 1, fontSize: 15 }}>
                                FREE{' '}
                                <Text style={{ fontWeight: 'normal', fontSize: 15 }}>
                                    with 50 mobile orders per month.
                                </Text>
                            </Text>
                        </View>
                    </View>;
                break;
            }
            default: {
                benefitComp =
                    <View style={{ alignSelf: 'stretch', marginTop: 16 }}>
                        <Text style={{ fontSize: 20, fontWeight: 'bold' }}>
                            Marketing
                        </Text>

                        <Text style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 8, lineHeight: 28 }}>
                            {
                                '• Calendar integration.\n' +
                                '• Location services/map visibility.\n' +
                                '• Control your profile.'
                            }
                        </Text>
                    </View>;
                break;
            }
        }

        const selectedAfterTrialTier = GLOBAL.DATA.USER['after_trial_tier'];
        const isFailedPayment = hasSubscriptionPaymentFailure();
        const lowerTier = tier < GLOBAL.DATA.USER['tier'];
        return(
            <View style={{ alignSelf: 'stretch', paddingTop: 24, paddingBottom: 48, paddingHorizontal: 32 }}>
                <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16 }}>
                    {tier > 0 ? `Everything in Tier ${tier - 1} plus:` : `What's included: `}
                </Text>

                {benefitComp}

                {/* Upgrade/Downgrade Button */}
                {
                    (!isFailedPayment && !freeTrialData.isFreeTrial && (tier > 0)) &&
                    <Button
                        onPress={() => sendUpgradeRequest(tier)}
                        style={{
                            marginTop: 24, backgroundColor: sameTier ? GLOBAL.COLOR.BASIC.UI_YELLOW : (lowerTier ? 'transparent' : GLOBAL.COLOR.BASIC.GREEN),
                            borderWidth: lowerTier ? 2 : 0, borderColor: GLOBAL.COLOR.DIVIDER
                        }}
                        text={sameTier ? 'Cancel Subscription' : `${lowerTier ? 'Downgrade' : 'Upgrade'} to ` + tierList[tier].title}
                        textStyle={{ color: lowerTier ? GLOBAL.COLOR.TEXT_GRAY : 'white' }}
                        isLoading={isLoadingPaymentFlow}
                        loadingText={'Loading data...'}
                    />
                }

                {/* Select Tier Button (for Free Trial users) */}
                {
                    (
                        (freeTrialData.isFreeTrial && (tier !== 0)) &&
                        (selectedAfterTrialTier > 0 ? (selectedAfterTrialTier === tier) : true)
                    ) &&
                    <Button
                        onPress={() => {
                            if (GLOBAL.DATA.USER['total_stripe_cards'] > 0) {
                                goUpgradeToNewTier(tier, null);
                            }
                            else savedPaymentModal.current?.showModal(tier);
                        }}
                        style={{ marginTop: 24, backgroundColor: GLOBAL.COLOR.BASIC.GREEN }}
                        text={selectedAfterTrialTier === tier ? `You selected ${tierList[tier].title}` : `Select ${tierList[tier].title}`}
                        textStyle={{ color: 'white' }}
                        locked={selectedAfterTrialTier === tier}
                        isLoading={isLoadingUpgrade}
                    />
                }
            </View>
        );
    }
    // endregion

    // region MAIN RENDER
    const freeTrialData = vendorFreeTrialCheck();
    const nextMonthMoment = useMemo(() => {
        const curMoment = moment();
        const subMoment = getSubscriptionDateStr(true);
        curMoment.set('date', subMoment.date());
        return curMoment.add(1, 'month');
    }, []);
    return(
        <View style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
            {/* Nav Bar */}
            <NavBar
                title={'Subscription Tier'}
                subTitle={'Powered by Stripe'}
                hideBorder={false}
                rightButtonIconName={'info'}
                rightButtonColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                rightButtonOnPress={() => showStripeInfo()}
            />

            {/* Same Price Notice */}
            {!isLoadingData && allSamePrice && renderSamePriceNotice()}

            {/* Next Payment Notice */}
            {
                (freeTrialData.isFreeTrial || (GLOBAL.DATA.USER['tier'] > 0)) &&
                <View
                    style={{
                        width: '100%', paddingHorizontal: 24, paddingVertical: 24, backgroundColor: '#FFA600',
                        flexDirection: 'row', alignItems: 'center'
                    }}>
                    <AwesomeIcon name={'info-circle'} size={28} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />
                    <Text style={{ fontSize: 16, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginHorizontal: 16, lineHeight: 20, flex: 1 }}>
                        {
                            freeTrialData.isFreeTrial ?
                                (
                                    freeTrialData.selectedTier ?
                                        `Your subscription will start on ${subDateStr} with a payment due of $${tierList[GLOBAL.DATA.USER['after_trial_tier']]?.price}.`
                                        :
                                        `Select your tier by ${subDateStr} so you don't lose access to your subscription.`
                                )
                                :
                                `Next payment scheduled on ${nextMonthMoment.format('MM/DD/YYYY')}`
                        }
                    </Text>
                </View>
            }

            {/* Logo */}
            <Image
                source={require('../../images/logo_truckster.png')}
                style={{ height: 34, marginTop: 32 }}
                resizeMode={'contain'}
            />

            <Image
                source={require('../../images/truckster_vendor_text_only.png')}
                style={{ height: 28, marginTop: 12 }}
                resizeMode={'contain'}
            />

            {/* Main Content */}
            {
                isLoadingData ?
                    <LoadingView isAbsolute={false} style={{ backgroundColor: 'white' }} />
                    :
                    <Animatable.View
                        animation={'fadeIn'}
                        style={{ marginTop: 24, flex: 1, width: '100%' }}>
                        <SectionList
                            ref={mainList}
                            style={{ flex: 1, width: '100%' }}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={{ paddingBottom: 24 + 56, paddingHorizontal: '25%' }}
                            sections={tierList}
                            renderItem={({ section, index }) => renderSectionItem(section.tier, index)}
                            renderSectionHeader={({ section }) => renderSectionHeader(section)}
                            keyExtractor={(item) => `item_tier${item.toString()}`}
                            onScrollToIndexFailed={() => {}}
                        />

                        {/* Gradient */}
                        <Image
                            source={require('../../images/image_gradient_down.png')}
                            style={{ position: 'absolute', top: -1, left: 0, width: '100%', height: 20 }}
                        />

                        {/* Gradient */}
                        <Image
                            source={require('../../images/image_gradient.png')}
                            style={{ position: 'absolute', bottom: -4, left: 0, width: '100%', height: 20 }}
                        />
                    </Animatable.View>
            }

            {/* Saved Payment Methods Modal */}
            <SavedPayment
                ref={savedPaymentModal}
                isSelectingAfterTrialTier={true}
                onCardSelectedForTier={(tier) => {
                    GLOBAL.DATA.USER['after_trial_tier'] = tier;
                    if (!!onCardSelectedForTier) {
                        onCardSelectedForTier(tier);
                    }
                    showAlert({
                        text: `Heard. You subscribed to ${getTierName(tier)}! Your payment of $${tierList[GLOBAL.DATA.USER['after_trial_tier']]?.price} will be processed on ${subDateStr}`
                    });

                    // Reload data
                    goFetchTierData();

                    // Refresh Profile Tab
                    setGlobalState(STATE_KEYS.PROFILE_REFRESH_TRIGGERED, true);
                }}
            />

            {/* Stripe Modal */}
            {renderStripeModal()}

            {/* Stripe Loading View */}
            {
                isLoadingPaymentFlow &&
                <LoadingView
                    isLoading={true}
                    description={'Loading Stripe Payment'}
                    isAbsolute={true}
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                    customTextStyle={{ color: 'white' }}
                />
            }

            {/* Other Loading Views */}
            {
                isLoadingUpgrade &&
                <LoadingView isLoading={true} description={'Processing your request'} isAbsolute={true} />
            }
        </View>
    );
    // endregion
};

SubscriptionTier.propTypes = {
    onCardSelectedForTier: PropTypes.func,
    onPaymentDone: PropTypes.func
}

export default SubscriptionTier;
